import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Loader2, Check, CircleDot, Send, ArrowRight, RotateCcw, Lightbulb,
} from "lucide-react";
import { useSimplify } from "../simplify-context";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const trackEvent = (copilot, kind, label) => {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ copilot, kind, label }),
    });
  } catch {}
};

const TONE = {
  amber: "border-amber-200 bg-amber-50",
  rose: "border-rose-200 bg-rose-50",
  teal: "border-brand-tealborder bg-brand-tealsoft",
};
const DOT = { amber: "text-amber-500", rose: "text-rose-500", teal: "text-brand-teal" };

function AnswerCard({ answer, testId }) {
  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-2xl bg-white border border-slate-200 shadow-[0_20px_40px_-15px_rgba(14,20,51,0.08)] overflow-hidden"
    >
      <div className="px-6 sm:px-8 pt-6 pb-5 border-b border-slate-100">
        <p className="font-display font-semibold text-lg sm:text-xl text-brand-navy leading-snug">
          {answer.summary}
        </p>
      </div>
      <div className="px-6 sm:px-8 py-5 space-y-6">
        {answer.sections.map((sec) => (
          <div key={sec.title}>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-3">
              {sec.title}
            </p>
            <ul className="space-y-2.5">
              {sec.items.map((item, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 + i * 0.12, duration: 0.45 }}
                  className={`rounded-xl border p-4 ${TONE[item.tone]}`}
                >
                  <div className="flex items-start gap-2.5">
                    <CircleDot size={15} className={`mt-1 shrink-0 ${DOT[item.tone]}`} />
                    <div>
                      <p className="text-sm font-semibold text-brand-navy leading-snug">{item.label}</p>
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mt-1">{item.detail}</p>
                    </div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        ))}
        {answer.actions?.length > 0 && (
          <div className="rounded-xl bg-brand-navy px-5 py-4 space-y-2">
            {answer.actions.map((a, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 + i * 0.15 }}
                className="flex items-start gap-2.5 text-sm text-slate-200"
              >
                <Check size={15} className="text-brand-gold mt-0.5 shrink-0" />
                {a}
              </motion.p>
            ))}
          </div>
        )}
      </div>
      {answer.sticky && (
        <div className="px-6 sm:px-8 py-4 bg-slate-50 border-t border-slate-100 flex items-start gap-2.5">
          <Lightbulb size={14} className="text-brand-teal mt-0.5 shrink-0" />
          <p className="text-xs text-slate-500 leading-relaxed italic">{answer.sticky}</p>
        </div>
      )}
    </motion.div>
  );
}

export default function CopilotDemo({ copilot }) {
  const { setOpen } = useSimplify();
  const [roleIdx, setRoleIdx] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | running | done
  const [toolsShown, setToolsShown] = useState([]);
  const [freeQ, setFreeQ] = useState("");
  const [freeAnswer, setFreeAnswer] = useState("");
  const [streaming, setStreaming] = useState(false);

  const role = copilot.roles[roleIdx];

  const runScenario = async (idx) => {
    if (phase === "running") return;
    trackEvent(copilot.id, "scenario", copilot.roles[idx].question);
    setRoleIdx(idx);
    setPhase("running");
    setToolsShown([]);
    setFreeAnswer("");
    const tools = copilot.roles[idx].tools;
    for (let i = 0; i < tools.length; i++) {
      await wait(620);
      setToolsShown((t) => [...t, tools[i]]);
    }
    await wait(650);
    setPhase("done");
  };

  const askFree = async (question) => {
    const q = (question || freeQ).trim();
    if (!q || streaming) return;
    trackEvent(copilot.id, "free", q);
    setStreaming(true);
    setFreeAnswer("");
    setFreeQ("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q, copilot: copilot.id }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const payload = p.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const { t } = JSON.parse(payload);
            if (t) setFreeAnswer((a) => a + t);
          } catch {}
        }
      }
    } catch {
      setFreeAnswer("The demo copilot is resting for a moment. Try one of the guided questions — they show the same magic.");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div data-testid={`demo-${copilot.id}`} className="rounded-3xl border border-slate-200 bg-[#F8FAFC] p-5 sm:p-8 lg:p-10">
      {/* role chips — Hick's law: max 4 */}
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-3">
        1 · Pick your seat
      </p>
      <div className="flex flex-wrap gap-2.5 mb-8">
        {copilot.roles.map((r, i) => (
          <button
            key={r.id + i}
            data-testid={`demo-role-${copilot.id}-${i}`}
            onClick={() => { setRoleIdx(i); setPhase("idle"); setFreeAnswer(""); }}
            aria-pressed={roleIdx === i}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
              roleIdx === i
                ? "bg-brand-navy text-white shadow"
                : "bg-white border border-slate-200 text-slate-600 hover:border-brand-teal hover:text-brand-navy"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* signature question — one large high-affordance target */}
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-3">
        2 · Ask the question this seat asks every week
      </p>
      <button
        data-testid={`demo-ask-${copilot.id}`}
        onClick={() => runScenario(roleIdx)}
        disabled={phase === "running"}
        className="group w-full text-left rounded-2xl bg-brand-navy text-white px-6 sm:px-8 py-6 sm:py-7 transition-all duration-300 hover:bg-brand-teal disabled:opacity-80 mb-6"
      >
        <span className="flex items-center justify-between gap-4">
          <span className="font-display font-semibold text-xl sm:text-2xl leading-snug">
            “{role.question}”
          </span>
          <span className="shrink-0 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
            {phase === "running" ? <Loader2 size={19} className="animate-spin" /> : <Sparkles size={19} />}
          </span>
        </span>
        <span className="block mt-2 text-xs text-slate-300 group-hover:text-white/80 transition-colors">
          Tap to watch the copilot work — about 5 seconds
        </span>
      </button>

      {/* live tool activity — Norman: continuous feedback on system state */}
      <AnimatePresence>
        {toolsShown.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="flex flex-wrap gap-2">
              {toolsShown.map((t, i) => (
                <motion.span
                  key={t}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-tealborder text-brand-teal font-mono text-[11px] px-3.5 py-1.5"
                >
                  <Check size={12} strokeWidth={3} />
                  {t}
                </motion.span>
              ))}
              {phase === "running" && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white font-mono text-[11px] px-3.5 py-1.5">
                  <Loader2 size={12} className="animate-spin" />
                  Thinking…
                </span>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* the peak moment: clear role-based answer */}
      {phase === "done" && <AnswerCard answer={role.answer} testId={`demo-answer-${copilot.id}`} />}

      {/* free exploration path — preserves information scent */}
      <div className="mt-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-3">
          3 · Ask your own question
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {copilot.suggestions.map((s) => (
            <button
              key={s}
              data-testid={`demo-suggestion-${copilot.id}-${s.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}
              onClick={() => askFree(s)}
              disabled={streaming}
              className="rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-600 px-4 py-2 transition-all duration-300 hover:border-brand-teal hover:text-brand-navy disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex gap-2.5">
          <input
            data-testid={`demo-free-input-${copilot.id}`}
            value={freeQ}
            onChange={(e) => setFreeQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && askFree()}
            placeholder={copilot.heroQuestion}
            className="flex-1 rounded-full border border-slate-200 bg-white px-5 py-3.5 text-sm text-brand-navy placeholder:text-slate-400 outline-none transition-colors focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20"
          />
          <button
            data-testid={`demo-free-submit-${copilot.id}`}
            onClick={() => askFree()}
            disabled={streaming || !freeQ.trim()}
            aria-label="Ask"
            className="shrink-0 w-12 h-12 rounded-full bg-brand-teal text-white flex items-center justify-center transition-all duration-300 hover:bg-brand-tealhover disabled:opacity-50"
          >
            {streaming ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>

        <AnimatePresence>
          {(freeAnswer || streaming) && (
            <motion.div
              data-testid={`demo-free-answer-${copilot.id}`}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-5 rounded-2xl bg-white border border-brand-tealborder p-6"
            >
              <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-brand-teal font-semibold mb-3">
                <Sparkles size={13} /> {copilot.name} · live answer
              </p>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed whitespace-pre-wrap">
                {freeAnswer.replace(/\*\*/g, "")}
                {streaming && <span className="inline-block w-2 h-4 bg-brand-teal/60 ml-1 animate-pulse" />}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* end-of-experience CTA — Peak-End rule */}
      <div className="mt-10 rounded-2xl bg-brand-navy text-white px-6 sm:px-8 py-7 flex flex-col sm:flex-row sm:items-center gap-5 justify-between">
        <div>
          <p className="font-display font-semibold text-lg leading-snug mb-1">
            This demo runs on sample data. Imagine it on yours.
          </p>
          <p className="text-sm text-slate-300">
            The first clear answer on your real numbers typically lands inside ten minutes.
          </p>
        </div>
        <div className="flex gap-3 shrink-0">
          <button
            data-testid={`demo-reset-${copilot.id}`}
            onClick={() => { setPhase("idle"); setToolsShown([]); setFreeAnswer(""); }}
            aria-label="Replay demo"
            className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-slate-300 hover:text-white hover:border-white/50 transition-all"
          >
            <RotateCcw size={17} />
          </button>
          <button
            data-testid={`demo-cta-${copilot.id}`}
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2.5 rounded-full bg-brand-teal text-white text-sm font-semibold px-7 py-3.5 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
          >
            See it on my numbers
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
